import { noop } from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';
import { UnstyledLink, Link } from '@zocdoc-frontend-common/components-link';
import {
    SignInForm,
    SignInFormWithMetric,
} from '@zocdoc-frontend-common/components-sign-in-form';
import { GaActionText } from '@zocdoc-frontend-common/components-sign-in-form/constants';
import { StyledLoginButton as LoginButton } from '@zocdoc-frontend-common/components-sign-in-form/SignInFormView';
import {
    FacebookLoginButtonTransparent,
    GoogleLoginButtonTransparent,
    AppleLoginButtonTransparent,
} from '@zocdoc-frontend-common/components-sociallogin';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { buildSsoLoginUrl } from '@zocdoc-frontend-common/utils-react/buildSsoLoginUrl';
import { blue70, slate40, slate70 } from '@zocdoc-mezzanine/foundations-colors';
import { EVENT_INITIATORS, INTERACTION_TYPES } from '@zocdoc/frontend-metrics';
import { TRACKING_ELEMENTS } from './constants';

export class LoginFormView extends React.Component {
    static propTypes = {
        facebookLoginUrl: PropTypes.string.isRequired,
        googleLoginUrl: PropTypes.string.isRequired,
        appleLoginUrl: PropTypes.string,
        defaultEmailValue: PropTypes.string,
        isProvider: PropTypes.bool,
        onClickFacebookLogin: PropTypes.func,
        onClickGoogleLogin: PropTypes.func,
        onClickAppleLogin: PropTypes.func,
        recordRememberMeAssignment: PropTypes.func,
        rememberMe: PropTypes.bool,
        returnUrl: PropTypes.string.isRequired,
        sendMetric: PropTypes.func,
        showRememberMe: PropTypes.bool,
        showSignUpLink: PropTypes.bool,
        showProviderLoginLink: PropTypes.bool,
        signedIn: PropTypes.bool,
        suggestedRedirect: PropTypes.string,
        trackClickFacebookLogin: PropTypes.func,
        trackClickGoogleLogin: PropTypes.func,
        trackClickAppleLogin: PropTypes.func,
        trackClickSignUp: PropTypes.func,
        useDefaultRedirectAfterSignInSuccess: PropTypes.bool,
        useModal: PropTypes.bool,
    };

    static defaultProps = {
        isProvider: false,
        onClickFacebookLogin: noop,
        onClickGoogleLogin: noop,
        onClickSignUp: noop,
        recordRememberMeAssignment: noop,
        sendMetric: noop,
        showRememberMe: false,
        showSignUpLink: false,
        showProviderLoginLink: false,
        signedIn: false,
        suggestedRedirect: null,
        trackClickFacebookLogin: noop,
        trackClickGoogleLogin: noop,
        trackClickSignUp: noop,
        useDefaultRedirectAfterSignInSuccess: true,
        useModal: false,
    };

    componentDidUpdate(prevProps) {
        const {
            isProvider,
            returnUrl,
            signedIn,
            suggestedRedirect,
            useDefaultRedirectAfterSignInSuccess,
        } = this.props;
        if (signedIn && prevProps.signedIn !== signedIn) {
            if (isProvider && suggestedRedirect) {
                window.location.assign(suggestedRedirect);
            } else if (useDefaultRedirectAfterSignInSuccess !== false) {
                window.location.assign(returnUrl);
            }
        }
    }

    render() {
        return (
            <div data-test="log-in-form-view">
                {this.renderSocialLogin()}
                {this.renderDivider()}
                {this.renderSignInForm()}
                {this.maybeRenderSignUpLink()}
                {this.maybeRenderProviderLoginLink()}
            </div>
        );
    }

    renderSocialLogin() {
        const {
            facebookLoginUrl: baseFacebookLoginUrl,
            googleLoginUrl: baseGoogleLoginUrl,
            rememberMe,
            returnUrl,
            showRememberMe,
        } = this.props;
        const facebookLoginUrl = buildSsoLoginUrl(
            baseFacebookLoginUrl,
            returnUrl,
            rememberMe,
            showRememberMe
        );
        const googleLoginUrl = buildSsoLoginUrl(
            baseGoogleLoginUrl,
            returnUrl,
            rememberMe,
            showRememberMe
        );
        return (
            <React.Fragment>
                <SsoLoginLink
                    beforeClick={this.handleClickGoogleLogin}
                    data-test="login-form-google-login-link"
                    href={googleLoginUrl}
                >
                    <GoogleLoginButtonTransparent message="Log in with Google" />
                </SsoLoginLink>
                <SsoLoginLink
                    beforeClick={this.handleClickFacebookLogin}
                    data-test="login-form-facebook-login-link"
                    href={facebookLoginUrl}
                >
                    <FacebookLoginButtonTransparent
                        message={'Log in with Facebook'}
                    />
                </SsoLoginLink>
                {this.maybeRenderAppleLoginButton()}
            </React.Fragment>
        );
    }

    // we won't enable this until ios13 ships
    maybeRenderAppleLoginButton() {
        const {
            appleLoginUrl: baseAppleLoginUrl,
            rememberMe,
            returnUrl,
            showRememberMe,
        } = this.props;
        const appleLoginUrl = buildSsoLoginUrl(
            baseAppleLoginUrl,
            returnUrl,
            rememberMe,
            showRememberMe
        );
        // note that we look at baseAppleLoginUrl here instead
        // of appleLoginUrl because we want to know if the prop was
        // set. buildSsoLoginUrl will always return something for
        // appleLoginUrl
        return baseAppleLoginUrl ? (
            <SsoLoginLink
                beforeClick={this.handleClickAppleLogin}
                data-test="login-form-apple-login-link"
                href={appleLoginUrl}
            >
                <AppleLoginButtonTransparent
                    /* note that this following line means the text changes
                    from 'Continue with Apple' to 'Log in with Apple'
                    to match the other buttons here.
                    */
                    message={'Log in with Apple'}
                />
            </SsoLoginLink>
        ) : null;
    }

    renderDivider() {
        return (
            <Divider>
                <DividerHorizontalLine />
                <DividerText>{`or`}</DividerText>
                <DividerHorizontalLine />
            </Divider>
        );
    }

    emailIsNotDeleted(email) {
        return typeof email === 'string' && !email.includes('deleted');
    }

    renderSignInForm() {
        const {
            recordRememberMeAssignment,
            sendMetric,
            showRememberMe,
            useModal,
            defaultEmailValue,
        } = this.props;
        const SelectedSignInForm =
            sendMetric === noop ? StyledSignInForm : StyledSignInFormWithMetric;
        return (
            <SelectedSignInForm
                defaultEmailValue={
                    this.emailIsNotDeleted(defaultEmailValue)
                        ? defaultEmailValue
                        : null
                }
                recordRememberMeAssignment={recordRememberMeAssignment}
                showRememberMe={showRememberMe}
                useModal={useModal}
            />
        );
    }

    maybeRenderProviderLoginLink() {
        const { showProviderLoginLink } = this.props;
        if (showProviderLoginLink) {
            return (
                <SignUpSection>
                    <SignUpLink
                        beforeClick={this.handleClickProviderLogin}
                        data-test="login-form-provider-login-link"
                        href="/signin?provider=1"
                    >
                        {`Provider login`}
                    </SignUpLink>
                </SignUpSection>
            );
        }
    }

    maybeRenderSignUpLink() {
        const { showSignUpLink } = this.props;
        if (showSignUpLink) {
            return (
                <SignUpSection>
                    <SignUpLink
                        beforeClick={this.handleClickSignUp}
                        data-test="login-form-signup-link"
                        href="/createuser/details"
                    >
                        {`New to Zocdoc? Create an account`}
                    </SignUpLink>
                </SignUpSection>
            );
        }
    }

    handleClickGoogleLogin = () => {
        const { sendMetric, onClickGoogleLogin, trackClickGoogleLogin } =
            this.props;
        trackClickGoogleLogin();
        sendMetric({
            action: GaActionText.trackClickGoogleLogin,
            element: TRACKING_ELEMENTS.googleLoginButton,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
        onClickGoogleLogin();
    };

    handleClickFacebookLogin = () => {
        const { sendMetric, onClickFacebookLogin, trackClickFacebookLogin } =
            this.props;
        trackClickFacebookLogin();
        sendMetric({
            action: GaActionText.trackClickFacebookLogin,
            element: TRACKING_ELEMENTS.facebookLoginButton,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
        onClickFacebookLogin();
    };

    handleClickAppleLogin = () => {
        const { sendMetric, onClickAppleLogin, trackClickAppleLogin } =
            this.props;
        trackClickAppleLogin();
        sendMetric({
            action: GaActionText.trackClickAppleLogin,
            element: TRACKING_ELEMENTS.appleLoginButton,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
        onClickAppleLogin();
    };

    handleClickSignUp = () => {
        const { sendMetric, trackClickSignUp } = this.props;
        trackClickSignUp();
        sendMetric({
            action: GaActionText.trackClickCreateAccount,
            element: TRACKING_ELEMENTS.signUpLink,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
    };

    handleClickProviderLogin = () => {
        const { sendMetric } = this.props;
        sendMetric({
            action: GaActionText.trackClickProviderLoginLink,
            element: TRACKING_ELEMENTS.providerLogin,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
    };
}

const SsoLoginLink = styled(UnstyledLink)`
    display: block;
    margin-bottom: 8px;
`;

const Divider = styled.div`
    align-items: center;
    display: flex;
    margin: 20px 0;
`;

const DividerText = styled.div`
    font: 16px/20px ${FontFamily.SharpSansMedium};
    color: ${slate70};
    margin: 0 11px;
`;

const DividerHorizontalLine = styled.div`
    border-bottom: 1px solid ${slate40};
    flex-grow: 1;
`;

const StyledLoginButton = css`
    ${LoginButton} {
        background-color: ${(p) => p.theme.components.button.primary.bgColor};
        border: 1px solid
            ${(p) => p.theme.components.button.primary.borderColor};
        color: ${(p) => p.theme.components.button.primary.color};
        &:hover,
        &:active {
            background-color: ${(p) =>
                p.theme.components.button.primary.hoverActiveBgColor};
            border-color: ${(p) =>
                p.theme.components.button.primary.hoverActiveBorderColor};
            color: ${(p) => p.theme.components.button.primary.hoverActiveColor};
            text-decoration: none;
        }
    }
`;

const StyledSignInForm = styled(SignInForm)`
    margin-bottom: 0;
    ${StyledLoginButton}
`;

const StyledSignInFormWithMetric = styled(SignInFormWithMetric)`
    margin-bottom: 0;
    ${StyledLoginButton}
`;

const SignUpSection = styled.div`
    margin-bottom: 20px;
`;

const SignUpLink = styled(Link)`
    font: 15px/20px ${FontFamily.SharpSansSemibold};
    color: ${blue70};
    border-bottom: 0;
`;
